// AppShell — the application shell (auth gate + the composed Padel24AppMain
// god-component-in-recovery). Moved out of the former monolith legacy/App.jsx
// (now deleted) into a fully type-checked + linted src/ module (PR-BI). The
// orchestration lives in src/state/* hooks, the presentational shell in
// src/app/* (AppHeader/GlobalOverlays/views); this file composes them.

import {
  markSingleMatchActivityCompleted,
  recordSingleMatchActivityLiveScore,
} from '../state/singleMatchActivityWrites';
import React, { useState, useEffect, useCallback } from 'react';
// ELO engine — moved out to typed modules under src/ (Phase 3, incremental split).
// EngineSettings is the SHARED mutable runtime singleton (same instance here and in elo.ts).
// Storage layer — moved out to a typed module under src/ (Phase 3, incremental split).
// Storage is the SHARED runtime singleton (same instance here and in storage.ts):
// the backend is chosen at module evaluation, and get(key) returns { value } — not a raw string.
// The tenant helpers (DEFAULT_TENANT, GLOBAL_STORAGE_KEYS, getTenantId, setTenantId,
// tenantScopeKey) are also exported from the module, but are not used here yet.
import { Storage } from '../lib/storage';
import { clubSession } from '../lib/auth';
import { useT } from '../i18n';
import { AccountView } from '../components/account/AccountView';
import {
  LinkBookingView,
  LinkActivitiesView,
  OrganizationAccessView,
  OrganizationAuditView,
  OrganizationLinksView,
} from '../components/organization';
import type { MeLink } from '../lib/api/me';
import { useOrganizationCapabilities } from '../state/useOrganizationCapabilities';
import { overrideRight, withOverrideRight } from '../lib/api/bookingOverride';
import { OverrideRightProvider } from '../state/overrideRight';
import { deleteThenCascade } from '../state/bookingCascade';
import { refundRight } from '../lib/api/staffRefund';
import { useRoleAvailability } from '../state/useRoleAvailability';
import { usePendingActions } from '../state/usePendingActions';
import { isApiMode } from '../lib/api/baseUrl';
import { portalMessagingAllowed } from '../lib/api/messagingAccess';
import { ORGANIZATION_ACCESS_MANAGE } from '../lib/api/organizationAccess';
import { ORGANIZATION_LINKS_MANAGE } from '../lib/api/organizationLinks';
import { ORGANIZATION_AUDIT_READ } from '../lib/api/organizationAudit';
import { currentImpersonation } from '../lib/api/impersonation';
// Shared time helpers (HH:MM <-> minutes) — moved out to a typed module under
// src/ (Phase 3, incremental split). Shared by booking, banepad and seriespill.
// defaultPadel24Dates/nextWeekendDates: pure default-date helpers (PR-AL).
import { defaultPadel24Dates, nextWeekendDates } from '../lib/date';
// Audio + speech cues for the Banepad/timer screens (pure browser-API side effects, PR-AL).
import { playEndSound, speakWarning } from '../lib/sound';
// Booking engine — moved out to a typed module under src/ (Phase 3, incremental
// split). Pure validation/creation/pricing/availability + booking config and
// the holiday tables the pricing rules depend on. The bookingEngine object and
// the Padel24Engine.booking wiring stay in this file (they reference UI helpers
// that have not moved yet).
import { validateBooking } from '../engine/booking';
// Klubbmesterskap (KM) engine — front-half pure cluster moved out to a typed
// module under src/ (Phase 3, incremental split). Match-format evaluation,
// group seeding, bracket builders, group standings, capacity/time-budget
// estimators, the tournament factory and the calendar scheduler. The
// klubbmesterskapEngine wiring object + Padel24Engine.klubbmesterskap stay below
// (mirrors the booking precedent). The back-half KM result/ELO functions
// (applyKMResult, computeKMClassStandings, recalcKMElo, propagateKMResults)
// remain interleaved among the React components — a separate later pass.
// Seriespill (league-play) engine — moved out to a typed module under src/
// (Phase 3, incremental split). Config/defaults, the round-robin division match
// builder, the configurable standings calculator, promotion/relegation, the
// tournament factory, the full season life-cycle (lock/reveal/transition/
// startNextSeason), data repair, the shared slot engine, and the pure
// match-court/date/time accessors. The seriespillEngine wiring object +
// Padel24Engine.seriespill assignment stay below (mirrors the booking/KM
// precedent). applySerieResult stays interleaved among the React components — it
// calls recalcKMElo (a back-half KM function not yet moved) — a separate later
// pass.
// Banepad engine — pure court-timeline/now helpers moved out to a typed module
// under src/ (Phase 3, incremental split). banepadTournamentView (frontend
// view-routing) and the BanepadCentralView/BanepadCourtScreen React components
// stay in this file.
// Americano family engines — pure clusters (Americano / Mexicano / Mixicano /
// Team Mexicano / Mix Americano / Club Americano / Club Team Americano / Club
// Mexicano) moved out to a typed module under src/ (Phase 3, incremental split).
// The 8 *Engine wiring objects + their Padel24Engine.<format> assignments stay
// in this file (mirrors the KM/booking precedent) and reference these imports.
// Purely-internal helpers (round-plan/cost/pool/seeding) are not re-imported
// into this file because nothing here references them directly.
// Padel24 engine — pure group/phase/finals/standings/promotion cluster moved out
// to a typed module under src/ (Phase 3, incremental split). LadderIcon (React),
// the Enkeltkamper single-match cluster, the ELO-weight display helpers,
// applyPadel24Result and the PlatformEngine/Padel24Engine facade stay in this
// file. timeToMinutes/minutesToTime are now imported from here (they live in the
// padel24 module and are used widely across this file).
import {
  ROUNDS_PER_PHASE,
  makeGroups,
  estimateSchedule,
  buildFullSchedule,
  initStandings,
  fixPlayerBackToBackConflicts,
  drawPhase1Matches,
} from '../engine/padel24';
// Shared UI primitives — extracted from the former monolith into typed components
// under ../src/components/ui (markup copied verbatim; styling unchanged). The
// definitions used to live inline in the monolith; they are imported here so
// every former call-site keeps working unchanged.
import { PinnedSidebar } from '../components/ui';
// Feature components extracted from the former monolith into typed component trees
// under ../src/components/<feature> (markup verbatim; styling unchanged).
// Tournament ranking / KM option tables — moved out of the former monolith to
// src/engine/constants (PR-AV). Still threaded as props into the setup/KM views.
import {
  RANKING_OPTIONS_PADEL24,
  RANKING_OPTIONS_KM,
  DEFAULT_RANKING_KM,
  KM_COURT_PRIORITY_OPTIONS,
  DEFAULT_KM_COURT_PRIORITY,
  KM_PACING_OPTIONS,
  RANKING_OPTIONS_AMERICANO,
  DEFAULT_RANKING_AMERICANO,
  KM_AVAILABLE_CATEGORIES,
  KM_CLASS_LETTERS,
  KM_DEFAULT_SHOWCASE,
  ALL_CLASSES,
} from '../engine/constants';
import { PORTAL_BRAND } from '../data/brand';
// Engine version-history data — moved out to a typed module under src/ (PR-AM).
// Seeded fallback player DB + the effective-DB merger + player history — src/engine (PR-AN/AO).
import { PLAYER_DB, getPlayerTournamentHistory } from '../engine/playerDb';
import { seedFallbackPlayers } from '../lib/api/players';
// The live PlatformEngine/Padel24Engine wiring object + the 11 format engines +
// banepadTournamentView — moved out of the former monolith to src/engine (PR-AO).
import { Padel24Engine, banepadTournamentView } from '../engine/platformEngine';
import { useTournamentRefresh } from '../state/useTournamentRefresh';
import { PadResultWaitingBanner } from '../components/ui';

/**
 * Views where a pad's result is visible, and therefore worth polling for.
 *
 * The banepad screens show the courts themselves; the tournament views show the
 * match list and the leaderboard computed from it — which is where the stale
 * copy was noticed. Everywhere else, a window focus is enough.
 */
const PAD_RESULT_VIEWS = new Set([
  'banepad',
  'padel24',
  'americano',
  'mexicano',
  'clubAmericano',
  'clubMexicano',
  'klubbmesterskap',
  'seriespill',
]);
import { useServerResultRecording } from '../state/useServerResultRecording';
// Pure result/label/adapter helpers for the tournament formats — moved out of the
// former monolith to typed sibling modules in src/engine (PR-AS). Still threaded as props.
import { applyAmericanoResult, americanoMatchToSlots } from '../engine/americanoResults';
import {
  applyPadel24Result,
  nextDisplayTournamentId,
  calcPlayerPositionInTournament,
} from '../engine/padel24Results';
// KM cluster + Seriespill result/ELO/standings/mutation helpers — moved out of the
// former monolith to typed sibling engine modules (PR-AT). Still threaded as props.
// (buildGroupMatches/syncGroupPairs/rebuildGroupMatches/kmRandomMatchScore/
// propagateKMResults are internal to kmResults.ts — not imported here.)
import {
  computeKMClassStandings,
  applyKMResult,
  rebuildKnockoutForDivision,
  swapPairsInTournament,
  unassignPairInTournament,
  findDuplicatePlayerInDivision,
  assignPairToGroup,
  movePairInTournament,
  addGroupInTournament,
  deleteGroupInTournament,
  applyKMRandomResults,
  recalcKMElo,
} from '../engine/kmResults';
import { applySerieResult } from '../engine/seriespillResults';
// Console-capture ring buffer (bug reports attach it) — moved to src/lib (PR-AP).
import { setupConsoleCapture } from '../lib/consoleCapture';
import { setupActivityCapture } from '../lib/activityCapture';
// Bug report/archive feature family — moved to src/components/bugs (PR-AP). The
// bug-report config (BUG_REPORT_EMAIL from VITE_* env) lives in
// src/components/bugs/config.ts (PR-BD) — read directly by BugReportModal, no
// secret props threaded. Web3Forms is retired (#1142).
import { BugArchiveView } from '../components/bugs';
// Tournament/match presentational components extracted into ../src/components/tournament.
// Explainer diagrams/charts (pure presentational SVG) extracted from the former monolith.
// Help/explainer documentation sections (static content composing the diagrams
// + Doc primitives) extracted from the former monolith into ../src/components/help.
// App changelog data (release log) — moved to a typed data module.
// Navigation view model — typed View union + live-tournament-view predicate.
import type { View } from './views/types';
import { useShellNavigation } from '../app/useNavigation';
import { useFocusOnViewChange } from './focusOnViewChange';
// The 8 persisted-state slices are now composed by StoreProvider and consumed
// via useStore() (PR-AE) — Padel24AppMain no longer calls the hooks directly.
import { useStore } from '../state/StoreProvider';
// Saved-setup ("drafts") list hook — lifted out of Padel24AppMain (PR-AQ).
import { useDrafts } from '../state/useDrafts';
import { useSingleMatchActions } from '../state/useSingleMatchActions';
import { usePlannedMatchActions } from '../state/usePlannedMatchActions';
import { useDerivations } from '../state/useDerivations';
import { useSeriesActions } from '../state/useSeriesActions';
import { useBookingActions } from '../state/useBookingActions';
import { useActivityActions } from '../state/useActivityActions';
import { useTournamentLifecycle } from '../state/useTournamentLifecycle';
import { AppHeader } from '../app/AppHeader';
import { useMessaging } from '../state/useMessaging';
import { CLUB_SENDER_ID, totalUnread } from '../engine/messaging';
import { GlobalOverlays } from '../app/GlobalOverlays';
import {
  HomeHubViews,
  LibraryViews,
  BookingActivityBanepadViews,
  MobileAppViews,
  ReportingViews,
  MessagesViews,
} from '../app/views';
// Loaded on demand, each in its own chunk, the first time one of its views
// opens (#1145, AUD-035) — same names and props as the eager groups.
import {
  InfoViews,
  KMViews,
  SeriespillViews,
  AmericanoSetupViews,
  AmericanoLiveViews,
  TournamentWizardViews,
} from '../app/views/lazyGroups';

export function AppShell({
  onLogout,
  // ── THE PLAYER APP'S CLUB CONTEXT (owner, 2026-10-16) ───────────
  //
  // Passed straight through to the player app. `activeClub` is the context
  // the player selected (null for none); `onSelectClub` changes it IN PLACE —
  // the store reloads the club's data and this shell stays mounted — and is
  // null where the club cannot change (a legacy member session).
  activeClub = null,
  onSelectClub = null,
  /** Organizations the account may administer — the in-app door to the portal. */
  organizations = [],
  requireLogin,
  devPersona,
  // ── THE SWITCHER'S ACTIONS (#752) ─────────────────────────────────
  //
  // Threaded rather than called here: selecting a scope AND a surface is the
  // gate's business, and `Padel24App` is where both already live. This file
  // is untyped (no props interface) by long standing, so they arrive as
  // ordinary optional parameters.
  onEnterOrganization,
  onPersonalHome,
}) {
  // The skip link's label is an ANNOUNCED string, so it goes through the
  // catalog rather than being typed in — even though this file is still on the
  // frozen i18n list, because a hard-coded accessible name is both an a11y and
  // an i18n defect (#781).
  const t = useT();
  // Navigation state-machine + browser-history sync + help routing lifted to
  // ../src/app/useNavigation (PR-AD). The hook owns view/setView, the two hash
  // history effects (+ popInProgress guard), and openHelp/helpReturnView/
  // helpInitialFormat. All call-sites below use the destructured names unchanged.
  // THE MEMBER SURFACE AND THE VIEW, from ONE hook (#847 review) — see
  // `useShellNavigation`. `standaloneMobile` is the native shell, `?native=1`,
  // OR a plain browser at `/#mobile`: start on the player app, fill the
  // viewport, render none of the portal's chrome. The phone frame is only the
  // portal's PREVIEW, which lives at `#mobile-preview`.
  const {
    memberSurface: standaloneMobile,
    view,
    setView,
    helpReturnView,
    helpInitialFormat,
    openHelp,
  } = useShellNavigation();

  // ── WHO MAY OPEN «TILGANGER» (slice 3, decision 4A) ──────────────
  //
  // The entry in the organization's settings is drawn only for an access
  // manager. Decided from `/me`'s per-organization capability union — the
  // same data the server's evaluator reads — never from the role keys in the
  // sign-in snapshot, which a custom role would never pass and which go stale
  // the moment somebody else changes a membership (#817). Re-asked each time
  // settings opens, so a right granted mid-session is seen the next time the
  // person looks. The server guards the route regardless; this only draws.
  const sessionAccountId = clubSession.state().account?.id ?? null;
  const sessionTenantId = clubSession.state().currentScope?.tenantId ?? null;
  const {
    capabilities: organizationCapabilities,
    linkBookingHosts,
    linkActivityHosts,
    refresh: refreshOrganizationCapabilities,
  } = useOrganizationCapabilities(sessionAccountId, sessionTenantId);
  // «BOOK BANER HOS PARTNERSENTERET» (slice 4c): the centre the person chose
  // on the home page. The door itself is drawn from `/me`'s link reach.
  const [linkHost, setLinkHost] = useState<MeLink | null>(null);
  // «AKTIVITETER HOS KLUBBEN» (slice 4d): the club the centre chose on its
  // home page, drawn from `/me` the same way.
  const [activityHost, setActivityHost] = useState<MeLink | null>(null);
  // …OR STARPOINT, ACTING AS THE ORGANIZATION (#976 decision 3, 2026-10-20).
  // An operator under an impersonation context holds no membership here, so
  // `/me` has nothing to report — but the server gives the context every
  // organization right, this page's included. The shell is re-keyed on the
  // context id (Padel24App), so reading it at render is current.
  const operatingAs = currentImpersonation();
  const canManageAccess =
    operatingAs !== null ||
    (organizationCapabilities?.includes(ORGANIZATION_ACCESS_MANAGE) ?? false);
  // ── THE CONTEXT `/auth/scopes` DOES NOT CARRY (owner, 2026-10-20/21) ──
  //
  // «Spiller» — a player profile — asked once per signed-in account. For
  // drawing only: the switcher offers what the server says this account holds,
  // and every request is still decided there. There is NO platform context:
  // `/sa` is its own entrance, signed in with Entra (#1002).
  const roleAvailability = useRoleAvailability(sessionAccountId);
  // «SAMARBEID» (slice 4a) — the same door from the same union, but NOT for an
  // operator under impersonation: the page's server re-reads the caller's own
  // memberships and refuses one, because Starpoint changes links in /sa
  // (decision 7 of 2026-10-18). Drawing a door the server refuses is the thing
  // these doors exist to avoid.
  const canManageLinks = organizationCapabilities?.includes(ORGANIZATION_LINKS_MANAGE) ?? false;
  // «LOGG» (#935, 2026-11-01) — the organization's own audit trail, for whoever
  // holds `organization.audit.read` here (Eier and Administrator by default)
  // or StarPoint acting as the organization, which the server gives every
  // organization right. Everybody else is refused by the server regardless.
  const canReadAuditLog =
    operatingAs !== null || (organizationCapabilities?.includes(ORGANIZATION_AUDIT_READ) ?? false);
  // THE «UTSTYR» CARD IN THE SETTINGS (the owner, 2026-10-21): the same
  // registry the booking calendar's admin bar opens, drawn for whoever holds
  // what its writes ask for — `asset.manage` since 2026-10-31 (#912), derived
  // from `court.manage` so the same people see it — or is StarPoint acting as
  // the organization. The server refuses everybody else regardless.
  const canManageEquipment =
    operatingAs !== null || (organizationCapabilities?.includes('asset.manage') ?? false);
  // MELDINGER (#236, owner decision 2026-10-31): the correspondence on `/kv`
  // is behind `messaging.send_as_organization` on read and write, so the icon,
  // the dock and the view are drawn — and the key polled — only for a session
  // the server would let read it. Off the API nothing is guarded and nothing
  // changes; see lib/api/messagingAccess.ts.
  // BOOKING AGAINST THE CLUB'S OWN RULES (#949): the `adminOverride` flag is
  // asked for only by a session holding `booking.override_constraints`, so a
  // role narrowed in /sa books tournaments, KM and activities under the
  // rules instead of having the whole reservation refused. Unknown (null)
  // sends the flag as asked and lets the server decide; see
  // lib/api/bookingOverride.ts.
  const mayOverrideConstraints = overrideRight({
    capabilities: organizationCapabilities,
    apiMode: isApiMode(),
    operatingAs: operatingAs !== null,
  });
  const canUseMessaging = portalMessagingAllowed({
    capabilities: organizationCapabilities,
    apiMode: isApiMode(),
    operatingAs: operatingAs !== null,
  });
  // FOCUS FOLLOWS THE VIEW (#1144, WCAG 2.4.3): to the new view's heading.
  useFocusOnViewChange(view);

  useEffect(() => {
    // Home too since slice 4c: the partner-courts door is drawn there.
    if (view === 'clubSettings' || view === 'home') refreshOrganizationCapabilities();
  }, [view, refreshOrganizationCapabilities]);

  // ── WHAT WAITS FOR WHOEVER RUNS THIS ORGANIZATION (owner, 2026-10-20) ──
  //
  // The header bell. Asked only inside an organization context on the API
  // backend — the personal home has no queue to be «yours», and the server
  // answers 403 for a role that may not read it, which hides the bell. The
  // view is the navigation key: coming back from the queue re-reads (throttled)
  // so a decision made there is reflected without waiting for the next tick.
  // StarPoint operating AS an organization (#976) has a context but no scope;
  // the transport addresses the organization from the impersonation, so the
  // context id stands in for the tenant.
  const pending = usePendingActions({
    enabled: isApiMode(),
    tenantId: sessionTenantId ?? operatingAs?.organization.id ?? null,
    navigationKey: view,
  });

  // Messaging (Meldinger): one slice for the header badge and the view — polled
  // slowly everywhere, quickly while the club is looking at its inbox.
  const messaging = useMessaging({
    enabled: canUseMessaging,
    live: true,
    pollMs: view === 'messages' ? 8000 : 30000,
  });
  const unreadMessages = totalUnread(messaging.state, CLUB_SENDER_ID);
  const [messagesDockOpen, setMessagesDockOpen] = useState(false);
  // Set the browser tab title (overrides index.html's <title> at startup).
  useEffect(() => {
    document.title = `${PORTAL_BRAND.name} — Turneringsplattform`;
  }, []);
  const [singleMatchesReturnView, setSingleMatchesReturnView] = useState<View>('otherFormats'); // where "back" from single matches should go
  // ── STATE CORE (PR-AE) ──────────────────────────────────────
  // The 8 persisted-state hooks + the toast system + banepadLockedCourt are
  // composed by ../src/state/StoreProvider and consumed here via useStore().
  // The atomic mount loader below + all mutators/derivations stay in this
  // component and use the destructured setters/refs exactly as before (the
  // sanctioned "loader-bridge" pattern). R1/§13.20 preserved: each slice keeps
  // its state+loaded-flag+persist effect together inside its hook; the loader
  // (async) flips the flags after the first read. StrictMode stays OFF (R2).
  const {
    // toast system
    toast,
    setToast,
    showToast,
    // tournaments (useTournaments) — §13.20 R1 flag-guarded trios
    tournament,
    setTournament,
    adoptServerTournament,
    activeTournLoaded: _activeTournLoaded,
    pausedTournaments,
    setPausedTournaments,
    pausedLoaded: _pausedLoaded,
    reportTournamentSaveFailure,
    // series (useSeries) — §13.20 R1 flag-guarded trios
    activeSeries,
    adoptServerSeries,
    setActiveSeries,
    activeSeriesLoaded: _activeSeriesLoaded,
    pausedSeries,
    setPausedSeries,
    pausedSeriesLoaded: _pausedSeriesLoaded,
    reportSeriesSaveFailure,
    // archive (useArchive)
    archive,
    archiveLoaded,
    // The member's OWN history, server-computed (#185). Separate from `archive`
    // on purpose — see `useArchive`.
    memberHistory,
    memberMe,
    setArchive,
    setArchiveLoaded: _setArchiveLoaded,
    deleteArchived,
    importArchive,
    // players / ELO (usePlayers)
    allPlayers,
    allPlayersRef,
    singleMatches,
    plannedMatches,
    eloAdjustments,
    setAllPlayers,
    setSingleMatches,
    setPlannedMatches,
    setEloAdjustments: _setEloAdjustments,
    saveAllPlayers: _saveAllPlayers,
    persistNewPlayerImmediate,
    addPlayer,
    updatePlayer,
    deletePlayer,
    // bookings (useBookings) — incl. the R5 bookingsRef sync mirror
    bookings,
    bookingsRef,
    setBookings,
    persistBookings,
    deleteBooking,
    deleteManyBookings,
    refreshBookings,
    syncBookingsFromPanel,
    // activities (useActivities)
    activities,
    applyServerActivity,
    adoptServerActivity,
    adoptServerActivities,
    refreshActivities,
    setActivities: _setActivities,
    persistActivities,
    // booking admin settings (useBookingSettings)
    bookingZones,
    bookingConfig,
    bookingHoursSaved,
    setBookingZones: _setBookingZones,
    setBookingConfig,
    updateBookingZones,
    updateBookingConfig,
    // equipment beside the courts (useAssets) — docs/domains/ASSET_BOOKING.md
    assets,
    assetBookings,
    addAssetBooking,
    adoptAssetBookings,
    addMemberAssetBooking,
    updateAssetBooking,
    removeAssetBooking,
    saveAsset,
    removeAsset,
    refreshAssets,
    dropAssetBookingsForBookings,
    // club (useClubInfo)
    clubInfo,
    // Whether it has been READ. The settings editor is gated on it (#775):
    // mounting that form against `DEFAULT_CLUB_INFO` let an empty save
    // overwrite a real club name.
    clubInfoStatus,
    saveClubInfo,
    // banepad kiosk-locked court
    banepadLockedCourt: _banepadLockedCourt,
    setBanepadLockedCourt: _setBanepadLockedCourt,
    // THE MEMBER CLUB LOAD (#185), finally read (#801 review).
    memberBootstrap,
  } = useStore();

  // A PLAYER FOUND BY NAME JOINS THIS SESSION'S LIST (#1060), so every picker
  // can find them. Nothing is written: they stay listed once one of the club's
  // events names them, which is the rule `GET /members/players` applies.
  const adoptPlayer = useCallback(
    (player: any) =>
      setAllPlayers((prev: any[]) =>
        prev.some((p) => String(p.id) === String(player.id)) ? prev : [...prev, player],
      ),
    [setAllPlayers],
  );

  /**
   * A BANEPAD CHANGED THE TOURNAMENT, AND THIS BROWSER FINDS OUT (#556).
   *
   * The active tournament was read once at mount and nothing ever asked again,
   * so a pad's result never reached the portal: «Kamper» showed the match
   * unplayed, the leaderboard was computed from a copy that predated it, and
   * re-entering the result got «allerede registrert» from a server that had it.
   *
   * `adoptServerTournament` is the right door — it marks the value as already
   * stored, so the blanket-persist effect does not write back what the pad just
   * wrote. Adopting through `setTournament` would re-persist it and spend a
   * conditional write for nothing.
   *
   * POLLED ONLY WHERE PAD RESULTS ARE ON SCREEN. Everywhere else a window focus
   * is enough, and paying for a tournament read every ten seconds on every
   * portal screen would be paid for by every club.
   */
  const padRefresh = useTournamentRefresh({
    tournament,
    onAdopt: (next) => adoptServerTournament(next),
    live: PAD_RESULT_VIEWS.has(view),
  });

  // useSeries (activeSeries/pausedSeries) -> destructured from useStore() above (PR-AE)
  const [serieHistoryFocus, setSerieHistoryFocus] = useState(null); // { classId, idx } | null — opens the season-history modal focused
  // pausedSeries + the series blanket-persist effects lifted to
  // ../src/state/useSeries (destructured above).
  // In-progress configurations — tournaments the user has begun setting up
  // but not started yet. These are saved to Storage by the setup components
  // (Padel24Wizard, AmericanoSetupView) and deleted when the tournament starts.
  // Format: { key, type ('padel24'|'americano'|'team_americano'), savedAt, name }
  // Pending setup-load: when the user clicks a saved setup on the
  // Home page, we store { key, setupId } here. The wizard reads this
  // on mount and loads the setup automatically.
  const [pendingSetupLoad, setPendingSetupLoad] = useState(null);
  // When a tournament setup is opened via «Opprett aktivitet» in Aktiviteter: remember it
  // so that «Tilbake» goes to Aktiviteter (and reopens the picker), not the tournament overview.
  const [setupFromActivities, setSetupFromActivities] = useState(false);
  const [reopenActivityPicker, setReopenActivityPicker] = useState(false);
  // Custom activity (coaching/event/custom) opened via the americano setup.
  const [customActivityType, setCustomActivityType] = useState(null);

  // Saved-setup ("drafts") list for the Home hub — state+handlers+effect lifted to a hook (PR-AQ).
  const {
    drafts,
    refreshDrafts: _refreshDrafts,
    deleteDraft,
    openDraft,
  } = useDrafts({
    view,
    setView,
    setPendingSetupLoad,
    setSetupFromActivities,
    setCustomActivityType,
  });
  // Open court booking directly on a specific date (from the 7-day availability strip on the start page).
  const [bookingInitialDate, setBookingInitialDate] = useState(null);
  // banepadLockedCourt (kiosk lock) -> destructured from useStore() above (PR-AE)
  // Sends «Tilbake» from a setup to Aktiviteter if it was opened from there, otherwise fallback.
  const exitSetupToActivities = (fallback) => {
    if (setupFromActivities) {
      setSetupFromActivities(false);
      setActivitiesOpenId(null);
      setReopenActivityPicker(true);
      setView('activities');
    } else if (fallback) {
      fallback();
    }
  };
  // When entering a hub view (creating a format directly), the via-Aktiviteter
  // flag is reset, so «Tilbake» from such setups behaves normally.
  useEffect(() => {
    if (view === 'createTournament' || view === 'miniTournaments') {
      setSetupFromActivities(false);
      setCustomActivityType(null);
    }
    if (view !== 'bookings') setBookingInitialDate(null);
  }, [view]);
  // «Rediger oppsett» for a started KM without results: { snapshot, tournamentId }
  const [editingKMSetup, setEditingKMSetup] = useState(null);
  // useArchive / useBookings / useActivities -> destructured from useStore() above (PR-AE).
  // (toast/showToast also moved to the store; helpReturnView/helpInitialFormat/openHelp -> useNavigation.)
  const [navPinned, setNavPinned] = useState(false); // whether the main menu should be pinned on the left side
  const [activitiesOpenId, setActivitiesOpenId] = useState(null); // deep link to waiting page
  const [serieBooking, setSerieBooking] = useState(null); // {match, seriesId, classId, title, discountPct, windowDays} — series reservation in the calendar
  const [serieFocusMatchId, setSerieFocusMatchId] = useState(null); // deep link from calendar → match program
  // AKTIVITETER: court reservation via the real booking calendar in a modal.
  // { seriesId, title } while the modal is open; new bookings are tagged to the activity.
  const [courtReservation, setCourtReservation] = useState(null);
  const [reservationDraft, setReservationDraft] = useState(null); // booking draft while the reservation modal is open
  // useBookingSettings (bookingZones/bookingConfig) -> destructured from useStore() above (PR-AE)
  const [profilePlayerId, setProfilePlayerId] = useState(null); // open player profile (popup from anywhere)
  // When opening a tournament FROM the player profile: remember which page + profile we
  // should return to when «Hjem»/«Tilbake» is clicked from the tournament.
  const [profileReturn, setProfileReturn] = useState(null); // { view, playerId } | null

  // Install global console capture as early as possible — used by the bug report
  // to attach error messages that may have occurred before the user clicked the
  // "Rapporter feil" button. Idempotent (checks __consoleCaptureInstalled__).
  // Activity capture rides along: the user's recent clicks/navigations and any
  // failed API calls, so a report says what led up to the bug.
  useEffect(() => {
    setupConsoleCapture();
    setupActivityCapture();
  }, []);

  // Klubb-info (clubInfo + saveClubInfo) now composed by StoreProvider (which
  // calls useClubInfo(showToast)) and destructured from useStore() above (PR-AE).

  // refreshBugsCount + the bugs-count poll effect removed (dead code, PR-AU):
  // unfixedBugsCount was set but never read — the bug menu badge it fed was
  // removed when Bug&forslag moved to the floating BugReportButton (see
  // NavMenuItems). The 5s Storage poll had no observable effect.

  // showToast + useClubInfo (clubInfo/saveClubInfo) + usePlayers (allPlayers/
  // singleMatches/eloAdjustments + player-CRUD mutators) -> composed by
  // ../src/state/StoreProvider, destructured from useStore() above (PR-AE).

  // ── SAVED SETUPS (IN-PROGRESS/REUSABLE TOURNAMENTS) ────
  // Loads saved setups from Storage. We used to auto-save the draft,
  // but now the user saves explicitly via the "💾 Lagre" button
  // in the wizard. These setup lists are named setups that can
  // be reused multiple times.
  // Keys:
  //   - padel24_setups_v1 (array of setups)
  //   - americano_setups_v1 (array of setups)
  //   - team_americano_setups_v1 (array of setups)
  //   - mexicano_setups_v1 (array of setups)
  //   - mixicano_setups_v1 (array of setups)
  // drafts: state + refreshDrafts/deleteDraft/openDraft + the home-refresh effect
  // -> src/state/useDrafts (PR-AQ, god-component collapse slice 1)

  // Open the full setup page for an EXISTING activity (edit all settings).
  // Mirrors openDraft, but passes the whole activity so the wizard loads it in edit mode.
  // openActivitySetup -> useActivityActions (src/state, PR-BE)

  // ── BROWSER HISTORY ─────────────────────────────────────────
  // The hash-segment (#view) history sync + popInProgress guard moved to
  // ../src/app/useNavigation (PR-AD). The two effects + the ref live in the hook.

  // ── ATOMIC MOUNT LOADER -> ../src/state/useInitialLoad (PR-AF) ──────
  // The cross-slice loader now lives in StoreProvider (useInitialLoad); it
  // flushes Storage into the slices via the store setters/refs and flips the
  // §13.20 loaded-flags. Padel24Engine is passed to <StoreProvider> by the auth gate.

  // Save all players to storage when the list changes.
  // saveAllPlayers / persistNewPlayerImmediate / addPlayer / updatePlayer /
  // deletePlayer lifted to ../src/state/usePlayers (destructured above).

  // ============================================================
  // SINGLE MATCHES (outside tournaments)
  // ============================================================

  // Register a single match + update the players' ELO
  // addSingleMatch/deleteSingleMatch/updateSingleMatch -> useSingleMatchActions (src/state, PR-AW)

  // persistBookings / deleteBooking / deleteManyBookings / syncBookingsFromPanel
  // lifted to ../src/state/useBookings (destructured above).

  // Court-booking write handlers (addBooking/addManyBookings/updateBooking/
  // replaceSeries) -> src/state/useBookingActions (PR-BA). Called here (early,
  // before topUpRollingSeries which uses addManyBookings); R5 bookingsRef mirror
  // + Storage-first via persistBookings preserved.
  const { addBooking, addManyBookings, updateBooking, replaceSeries } = useBookingActions({
    bookingsRef,
    bookings,
    setBookings,
    persistBookings,
    bookingConfig,
    clubInfo,
    Padel24Engine,
    onBookingPlaced: refreshAssets,
    // A member's machines, as the server wrote them (#911 slice 2) — adopted
    // by id, so a replayed command never shows a machine twice.
    onMemberAssetBookings: (rows) => adoptAssetBookings(rows as any[]),
    mayOverrideConstraints,
    t,
  });
  // Activity (signup/series) action handlers -> src/state/useActivityActions
  // (PR-BE). Called AFTER useBookingActions so addManyBookings is in scope (TDZ).
  const {
    openActivitySetup,
    openActivityReservation,
    closeActivityReservation,
    handleReserveTournament,
    buildActivityOnCreate,
    applySeriesEdit,
    seriesEditPending,
    setSeriesEditPending,
    seriesEditConfirm,
    setSeriesEditConfirm,
  } = useActivityActions({
    activities,
    persistActivities,
    adoptServerActivity,
    adoptServerActivities,
    refreshActivities,
    refreshBookings,
    bookings,
    bookingsRef,
    addManyBookings,
    deleteManyBookings,
    showToast,
    setView,
    setSetupFromActivities,
    setCustomActivityType,
    setEditingKMSetup,
    setPendingSetupLoad,
    setActivitiesOpenId,
    courtReservation,
    setCourtReservation,
    reservationDraft,
    setReservationDraft,
  });

  // Batch creation: add many bookings with ONE Storage write and ONE
  // setState. Used for large recurring series — going through addBooking in a
  // loop would give N writes + N re-renders and could hang the browser.
  // overrides: list of existing booking ids that should be deleted first.
  // addManyBookings -> src/state/useBookingActions (PR-BA); see hook call above

  // ROLLING RECURRING: top up future occurrences for «fortløpende» (ongoing) series,
  // so there are always activities up to the horizon (with no end date).
  // rollingToppedRef + topUpRollingSeries (+ effect) -> useActivityActions (PR-BE)

  // RECURRING EDITING (Phase 2): when a series activity is saved, choose the scope.
  // Settings (config) + location propagate; each occurrence keeps its own date/booking.
  // seriesEditPending/seriesEditConfirm state -> useActivityActions (PR-BE)
  // applySeriesEdit -> useActivityActions (PR-BE)

  // updateBooking -> src/state/useBookingActions (PR-BA); see hook call above

  // persistActivities lifted to ../src/state/useActivities (destructured above).

  // AKTIVITETER: default booking draft for a tournament — fixed date (not
  // recurring), recurrence can be turned on in the panel (e.g. every other Friday).
  // defaultActivityDraft -> useActivityActions (PR-BE, internal)

  // AKTIVITETER: open/close the reservation modal (shared seriespill panel).
  // openActivityReservation -> useActivityActions (PR-BE)
  // closeActivityReservation -> useActivityActions (PR-BE)
  // syncBookingsFromPanel lifted to ../src/state/useBookings (destructured above).

  // ── SHARED ACTIVITY HELPERS (additive) ──────────────────────────────────
  // Used by all setup views that should be able to run VIA AKTIVITETER
  // (the signup base v1). The reserve + activity-mode branches are format-agnostic —
  // they read tournamentType from args. launch() handles the format-specific part
  // (engine + view) when it is NOT activity mode.
  // handleReserveTournament -> useActivityActions (PR-BE)
  // buildActivityOnCreate -> useActivityActions (PR-BE)

  // ── AUTO-CANCELLATION ──────────────────────────────────────────────────
  // When the signup deadline (signupCloseHours before start) has passed AND the
  // minimum has not been reached, the activity is auto-cancelled: the bookings are
  // released and paid players are marked refunded. Runs on load + every minute.
  // activity auto-cancel effect -> useActivityActions (PR-BE)

  // Batch editing of a series: replace the entire series with a new set of bookings.
  // removeSeriesId is removed first, then new ones are validated and added — all in one
  // Storage write. Used when changing common fields OR the pattern in a series.
  // replaceSeries -> src/state/useBookingActions (PR-BA); see hook call above

  // updateBookingZones / updateBookingConfig lifted to
  // ../src/state/useBookingSettings (destructured above). The atomic mount
  // loader still flushes loaded values via setBookingZones/setBookingConfig.

  // The wizard returns a fully configured tournament → goes to 'ready' status, show active view
  // handleWizardComplete -> useTournamentLifecycle (PR-BF)

  // Auto-park the existing active tournament without asking the user.
  // Used when the user starts a NEW tournament while one is already running —
  // the existing one is sent to pausedTournaments so the user can have several
  // ongoing tournaments at the same time (one active, the rest parked).
  //
  // HANDLES CORRUPT TOURNAMENTS: If the active tournament is missing matches/groups,
  // it is NOT parked (there is no point keeping an incomplete one).
  // It is silently deleted and frees the slot for a new tournament.
  // Save edited preview groups back to the signup activity,
  // as an ordered list of player ids. The next preview restores the
  // order (newly registered players are placed at the back). This is how changes are «saved» in the preview.
  // savePreviewGroupsToActivity -> useTournamentLifecycle (PR-BF)

  // parkCurrentTournament -> useTournamentLifecycle (PR-BF)

  // Padel24 preview → commit: when a previewed tournament is actually started
  // (status goes 'ready' → 'running'), remove the activity and strip the preview flag.
  // preview->commit effect -> useTournamentLifecycle (PR-BF)

  // When the user goes back to home
  // goHome -> useTournamentLifecycle (PR-BF)

  // Save tournament to archive (when it completes or manually)
  // archiveTournament -> useTournamentLifecycle (PR-BF)

  // Import archived tournaments from JSON (backup-restore)
  // importArchive lifted to ../src/state/useArchive (destructured above).

  // Reset / start over — deletes without archiving
  // resetTournament -> useTournamentLifecycle (PR-BF)

  // Navigates to another view, but auto-parks the tournament if it is active.
  // Used for the nav menu, the "home" button at the top, and other places the user
  // might navigate away from an active tournament without explicitly ending it.
  // navigateWithAutoPause -> useTournamentLifecycle (PR-BF)

  // Persistence for paused tournaments
  // persistPaused (internal) -> useTournamentLifecycle (PR-BF)

  // Result entry from the banepad central screen (the pad). Writes to the correct tournament:
  // the active one (setTournament) or a paused/parked one (pausedTournaments). Padel24 only.
  // recordResultForTournament -> useTournamentLifecycle (PR-BF)

  // KM result from the banepad pad (game/set). Writes to the active or paused KM tournament.
  // recordKMResultForTournament -> useTournamentLifecycle (PR-BF)

  // Seriespill result from the banepad. Writes to the active series (storage-first via
  // updateActiveSeries). Uses the same pure applySerieResult as SeriespillView.
  // recordSerieResultForTournament/updateActiveSeries/handlePause+Resume+DeletePausedSeries
  // -> src/state/useSeriesActions (PR-AY); called after useDerivations below (reads effectivePlayerDB).

  // Pause active tournament — move to pausedTournaments and free the tournament slot
  // handlePauseTournament -> useTournamentLifecycle (PR-BF)

  // Resume paused tournament — move from pausedTournaments to tournament
  // If there is already an active tournament, park it first
  // handleResumeTournament -> useTournamentLifecycle (PR-BF)

  // Set an ONGOING tournament in the list to paused (running → paused) without opening
  // it — moves it from «Pågående» to «Pausede» on the main page.
  // handlePauseRunningInList -> useTournamentLifecycle (PR-BF)

  // Save and close a tournament from the main page (Pågående/Pausede) → the archive,
  // without opening it. Can be reopened from the archive with «Fortsett turneringen».
  // handleArchiveFromList -> useTournamentLifecycle (PR-BF)

  // Delete a paused tournament for good (without resuming)
  // handleDeletePaused -> useTournamentLifecycle (PR-BF)

  // Delete ALL paused tournaments in one operation. Cannot be done by
  // calling handleDeletePaused in a loop — each call sees the same old
  // pausedTournaments (closure) and overwrites the others.
  // handleDeleteAllPaused -> useTournamentLifecycle (PR-BF)

  // Compute the effective player DB based on allPlayers + archive ELO history
  // ============================================================
  // LIVE-ALLPLAYERS — authoritative "current ELO" across all formats.
  //
  // Problem: allPlayers is updated ONLY when a tournament is archived. If a
  // player is in an active or paused tournament, they have already received
  // ELO changes in tournament.players[i].elo, but not in allPlayers.
  // The result: the player database and performance view show stale ELO.
  //
  // Solution: build a virtual "live" allPlayers that takes allPlayers as the base
  // and adds the ELO delta from each active/paused tournament where the player
  // participates. Delta = tournament.players[i].elo - tournament.players[i].startElo.
  //
  // This covers ALL tournament formats (Padel24, Americano, Mexicano etc.)
  // since they all set startElo per player at creation and update
  // tournament.players[i].elo after each match. Single matches affect
  // allPlayers directly and need no delta adjustment.
  // ============================================================
  // Derived data (liveAllPlayers/effectivePlayerDB/combinedArchive/archiveWithLive/
  // archiveForArchive) — lifted to src/state/useDerivations (PR-AX). Pure useMemo.
  const { liveAllPlayers, effectivePlayerDB, combinedArchive, archiveWithLive, archiveForArchive } =
    useDerivations({
      allPlayers,
      tournament,
      pausedTournaments,
      archive,
      singleMatches,
      activeSeries,
    });

  // Single-match (enkeltkamp) CRUD handlers — src/state/useSingleMatchActions (PR-AW).
  // Placed after useDerivations for the other derived values it takes;
  // addSingleMatch no longer reads `liveAllPlayers` (#242).
  const { addSingleMatch, deleteSingleMatch, updateSingleMatch } = useSingleMatchActions({
    allPlayers,
    setAllPlayers,
    singleMatches,
    setSingleMatches,
  });

  // Planned (booking-linked, not-yet-played) single matches — src/state/usePlannedMatchActions.
  const {
    addPlannedMatch,
    updatePlannedMatchPlayers,
    updatePlannedMatchLiveScore,
    deletePlannedMatch,
    deletePlannedMatchesByBookingId,
    completePlannedMatch,
  } = usePlannedMatchActions({ plannedMatches, setPlannedMatches });

  // Booking deletion must also drop any planned match linked to that booking.
  // Cross-domain cascade lives here (the booking slice stays unaware of planned
  // matches); the plan cleanup is best-effort and never blocks the deletion.
  // Only once the booking actually went — a refused delete keeps its plan (#284,
  // state/bookingCascade.ts).
  const deleteBookingAndPlans = (id: any) =>
    deleteThenCascade(
      () => deleteBooking(id),
      () => deletePlannedMatchesByBookingId(id),
    );
  const deleteManyBookingsAndPlans = (ids: any) =>
    deleteThenCascade(
      () => deleteManyBookings(ids),
      () => deletePlannedMatchesByBookingId(ids),
    );

  // «AVBESTILL OG REFUNDER» (#284): the calendar's refund button is a door,
  // drawn only for a session holding `payment.refund` — unknown is no. See
  // lib/api/staffRefund.ts. Once the server has refunded, the booking is gone
  // (and its equipment with it, ON DELETE CASCADE), so the calendar re-reads.
  const staffRefund = {
    allowed: refundRight({
      capabilities: organizationCapabilities,
      apiMode: isApiMode(),
      operatingAs: operatingAs !== null,
    }),
    onRefunded: async (bookingId: string) => {
      await refreshBookings();
      await dropAssetBookingsForBookings([bookingId]);
    },
  };

  // Via-aktiviteter single-match: create the open enkeltmatch-activity through
  // the existing activity-create path (status awaiting_signup, registrations:[],
  // navigates to Aktiviteter). No tournament launch is needed for an activity.
  const createLinkedActivity = async ({ activityId, config }: any) =>
    buildActivityOnCreate(() => {})({
      config,
      players: [],
      activityMode: 'activity',
      activityId,
      bookingDraft: null,
    });

  // The deps the two shared enkeltmatch activity writers need (#245). One
  // object, so the pad and the admin app pass the same thing.
  const activityWriteDeps = { activities, persistActivities, applyServerActivity, showToast };

  // Complete a single-match activity: run the captured best-of-3 through the
  // existing single-match ELO pipeline, then mark the activity done + link the
  // resulting match. The linked court booking is left untouched.
  const completeSingleMatchActivity = async (activity: any, data: any) => {
    // A local planned match (pad-normalised, tagged __planId) finalises through the
    // plan pipeline (ELO + drops the plan), not the activity pipeline.
    if (activity?.__planId) {
      return completePlannedMatch(activity.__planId, data, addSingleMatch);
    }
    // Carry the activity's match rules (booking-time defaults) into the match.
    const res = await addSingleMatch({
      ...data,
      format: activity?.config?.matchFormat || data?.format || 'bo3_super',
      deuceMode: activity?.config?.deuceMode || 'advantage',
    });
    if (!res?.success) return res || { success: false, error: 'ukjent feil' };
    await markSingleMatchActivityCompleted(
      activityWriteDeps,
      activity,
      res.match?.id != null ? String(res.match.id) : null,
    );
    return res;
  };

  // Live, continuous score for an enkeltmatch — persisted on every change (NOT
  // finalised, no ELO) so the pad and the central grid show the running standing
  // and it survives reload. Finalising (ELO) is completeSingleMatchActivity.
  const recordSingleMatchLiveScore = async (activity: any, liveScore: any) => {
    // Local planned match → persist on the plan store (routed by the __planId tag).
    if (activity?.__planId) {
      await updatePlannedMatchLiveScore(activity.__planId, liveScore);
      return;
    }
    await recordSingleMatchActivityLiveScore(activityWriteDeps, activity, liveScore);
  };

  // Seriespill action handlers — src/state/useSeriesActions (PR-AY). After
  // useDerivations because recordSerieResultForTournament reads effectivePlayerDB.
  const {
    recordSerieResultForTournament,
    recordSerieLiveScore,
    updateActiveSeries,
    handlePauseSeries,
    handleResumeSeries,
    handleDeletePausedSeries,
  } = useSeriesActions({
    activeSeries,
    setActiveSeries,
    pausedSeries,
    setPausedSeries,
    showToast,
    setView,
    effectivePlayerDB,
    reportSeriesSaveFailure,
  });
  // Tournament-lifecycle handlers -> src/state/useTournamentLifecycle (PR-BF).
  // Called AFTER useDerivations so effectivePlayerDB is in scope (TDZ);
  // persistPaused stays private to the hook.
  const {
    handleWizardComplete,
    savePreviewGroupsToActivity,
    parkCurrentTournament,
    goHome,
    archiveTournament,
    resetTournament,
    navigateWithAutoPause,
    recordResultForTournament,
    recordKMResultForTournament,
    recordKMLiveScore,
    recordPadLiveScore,
    handlePauseTournament,
    handleResumeTournament,
    handlePauseRunningInList,
    handleArchiveFromList,
    handleDeletePaused,
    handleDeleteAllPaused,
    openArchived,
    resumeArchivedTournament,
  } = useTournamentLifecycle({
    adoptServerActivity,
    tournament,
    setTournament,
    pausedTournaments,
    setPausedTournaments,
    archive,
    setArchive,
    allPlayers,
    allPlayersRef,
    setAllPlayers,
    activities,
    persistActivities,
    view,
    setView,
    setToast,
    showToast,
    reportTournamentSaveFailure,
    effectivePlayerDB,
    profileReturn,
    setProfileReturn,
    setProfilePlayerId,
    setSingleMatchesReturnView,
    setSerieHistoryFocus,
    setActivitiesOpenId,
  });

  // ── SERVER-OWNED RESULT RECORDING (#404, PR 2) ────────────────────
  //
  // The portal used to apply a result in the browser and write the club's
  // whole session document back. The server owns it now: same engine applier,
  // plus an exactly-once claim and the players' global rating commit, in one
  // transaction. `null` on the in-memory backend, where there is no server to
  // own anything — the views then keep their existing local path.
  const { recordServerResult, resultsAreServerAuthoritative } = useServerResultRecording();
  const recordServerScore = async (matchId: any, score1: number, score2: number) =>
    recordServerResult({ sessionId: String(tournament?.id ?? ''), matchId, score1, score2 });
  const recordPadel24Result = resultsAreServerAuthoritative()
    ? async (matchId: any, score1: number, score2: number) => {
        await recordServerScore(matchId, score1, score2);
      }
    : null;
  // Americano needs the applied SESSION back: its freeze-modal logic reasons
  // about the match list after the result, and that list is the server's now.
  const recordAmericanoResult = resultsAreServerAuthoritative() ? recordServerScore : null;

  // klubbmesterskap and seriespill carry a STRUCTURED score rather than two
  // numbers, and the session they belong to differs — the KM is the active
  // tournament, the series is its own document (#405).
  const recordKMResult = resultsAreServerAuthoritative()
    ? async (matchId: any, score: any) =>
        recordServerResult({ sessionId: String(tournament?.id ?? ''), matchId, score })
    : null;
  const recordSerieResult = resultsAreServerAuthoritative()
    ? async (matchId: any, score: any) =>
        recordServerResult({ sessionId: String(activeSeries?.id ?? ''), matchId, score })
    : null;

  // Open an archived tournament (read-only mode via flag)
  // For the virtual single-matches "tournament": redirect to the single-matches page instead
  // openArchived -> useTournamentLifecycle (PR-BF)

  // Take an archived (unfinished) tournament out of the archive and make it active again,
  // so it shows as «Pågår» on the main page and can be continued. The archive copy
  // is kept as a backup (same archiveId) and updated when the user saves again.
  // resumeArchivedTournament -> useTournamentLifecycle (PR-BF)

  // deleteArchived lifted to ../src/state/useArchive (destructured above).

  // When status becomes 'done' (the tournament is finished): we do NOT auto-save.
  // The user gets the choice to click "Lagre og avslutt" or "Slett uten å lagre"
  // via the Avslutt button in the tournament view.

  return (
    // The override right for the writers below that do not go through
    // useBookingActions — the series reservation panel (#949).
    <OverrideRightProvider value={mayOverrideConstraints}>
      <div
        className="min-h-screen bg-bg text-ink font-body"
        // Room at the end of the page for the «new version» banner, which is
        // fixed to the bottom and would otherwise cover the last row of content.
        // Zero while it is not showing (`NEW_BUILD_BANNER_HEIGHT_VAR`).
        style={{ paddingBottom: 'var(--new-build-banner-h, 0px)' }}
      >
        {/* ── THE FIRST THING A KEYBOARD USER NEEDS (#781) ──────────────
          WCAG 2.4.1, Level A, and the target already existed: `<main>` is
          below, and before this a keyboard user had to tab through the whole
          header and navigation on every view to reach it.

          Visible only when focused — `sr-only` until `focus:` undoes it, which
          is the standard shape. It is the first element in the tree so it is
          the first tab stop, which is the whole point.

          Its focused COLOURS are `.skip-link` in index.css (#899): they were
          `focus:bg-ink focus:text-paper`, which compile to nothing because
          --ink and --paper are hand-written classes Tailwind cannot put a
          variant on, so the link surfaced as bare text over the header. */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only skip-link focus:fixed focus:top-2 focus:left-2 focus:z-[10001] focus:rounded focus:px-3 focus:py-2 focus:no-underline"
        >
          {t('ui.skipToContent')}
        </a>
        {/* Design system moved to src/index.css (PR-BG) — loaded before any render
          (incl. login), so the runtime <Styles/> injection is gone. */}
        {/* A pad's result the portal will NOT take on its own (#556) — it touches
          a match somebody is typing into. It offers; it never counts down. */}
        <PadResultWaitingBanner pending={padRefresh.pending} onAccept={padRefresh.accept} />
        {!standaloneMobile && (
          <AppHeader
            view={view}
            navigateWithAutoPause={navigateWithAutoPause}
            navPinned={navPinned}
            setNavPinned={setNavPinned}
            combinedArchive={combinedArchive}
            Padel24Engine={Padel24Engine}
            setView={setView}
            clubInfo={clubInfo}
            tournament={tournament}
            requireLogin={requireLogin}
            onLogout={onLogout}
            unreadMessages={unreadMessages}
            onOpenMessages={
              canUseMessaging
                ? () => (view === 'messages' ? undefined : setMessagesDockOpen((v) => !v))
                : undefined
            }
            pendingActions={pending.actions}
            onPendingAction={(target) => navigateWithAutoPause(target)}
            // ── THE SWITCHER'S INPUTS (#752) ────────────────────────────
            //
            // `scopes` is `/auth/scopes` — where this account may ACT. NOT
            // `/me/clubs`, which answers «where am I on the roster»: an
            // administrator with no player profile must still reach their
            // workspace, and a role must never invent roster membership (owner
            // ruling, 2026-10-15).
            //
            // Read from the session on each render rather than captured once:
            // the scope changes under this component when somebody switches,
            // and a captured list would keep ticking the one they left.
            scopes={clubSession.state().scopes}
            currentTenantId={clubSession.state().currentScope?.tenantId ?? null}
            onEnterOrganization={onEnterOrganization}
            onPersonalHome={onPersonalHome}
            hasPlayerProfile={roleAvailability.player}
          />
        )}

        {/* Flex layout: sidebar (pinned) + main side by side */}
        <div className="max-w-7xl mx-auto flex gap-2">
          {navPinned && !standaloneMobile && (
            <PinnedSidebar
              currentView={view}
              onNavigate={navigateWithAutoPause}
              archiveCount={combinedArchive.length}
              onUnpin={() => {
                setNavPinned(false);
                try {
                  localStorage.setItem('padel24_nav_pinned', 'false');
                } catch {
                  /* ignore */
                }
              }}
              Padel24Engine={Padel24Engine}
            />
          )}
          <main id="main-content" tabIndex={-1} className="flex-1 min-w-0">
            <HomeHubViews
              view={view}
              goHome={goHome}
              setView={setView}
              clubInfo={clubInfo}
              bookingConfig={bookingConfig}
              clubInfoStatus={clubInfoStatus}
              onOpenAccess={canManageAccess ? () => setView('access') : null}
              onOpenLinks={canManageLinks ? () => setView('links') : null}
              onOpenAuditLog={canReadAuditLog ? () => setView('auditLog') : null}
              equipment={
                canManageEquipment
                  ? { assets, config: bookingConfig, onSave: saveAsset, onDelete: removeAsset }
                  : null
              }
              partnerCourts={linkBookingHosts}
              onOpenPartnerCourts={(host) => {
                setLinkHost(host);
                setView('linkBooking');
              }}
              partnerActivities={linkActivityHosts}
              onOpenPartnerActivities={(host) => {
                setActivityHost(host);
                setView('linkActivities');
              }}
              saveClubInfo={saveClubInfo}
              parkCurrentTournament={parkCurrentTournament}
              setTournament={setTournament}
              setSetupFromActivities={setSetupFromActivities}
              setCustomActivityType={setCustomActivityType}
              setPendingSetupLoad={setPendingSetupLoad}
              setEditingKMSetup={setEditingKMSetup}
              setSingleMatchesReturnView={setSingleMatchesReturnView}
              singleMatches={singleMatches}
              setBookingInitialDate={setBookingInitialDate}
              setActivitiesOpenId={setActivitiesOpenId}
              activities={activities}
              bookings={bookings}
              tournament={tournament}
              resetTournament={resetTournament}
              archiveTournament={archiveTournament}
              showToast={showToast}
              combinedArchive={combinedArchive}
              archiveLoaded={archiveLoaded}
              openArchived={openArchived}
              deleteArchived={deleteArchived}
              effectivePlayerDB={effectivePlayerDB}
              pausedTournaments={pausedTournaments}
              handleResumeTournament={handleResumeTournament}
              handlePauseRunningInList={handlePauseRunningInList}
              handleArchiveFromList={handleArchiveFromList}
              handleDeletePaused={handleDeletePaused}
              handleDeleteAllPaused={handleDeleteAllPaused}
              drafts={drafts}
              openDraft={openDraft}
              deleteDraft={deleteDraft}
              activeSeries={activeSeries}
            />
            {/* players view -> LibraryViews (PR-AJ) */}
            <LibraryViews
              view={view}
              archiveForArchive={archiveForArchive}
              effectivePlayerDB={effectivePlayerDB}
              goHome={goHome}
              openArchived={openArchived}
              deleteArchived={deleteArchived}
              importArchive={importArchive}
              setProfilePlayerId={setProfilePlayerId}
              pausedTournaments={pausedTournaments}
              handleResumeTournament={handleResumeTournament}
              handleDeletePaused={handleDeletePaused}
              serieHistoryFocus={serieHistoryFocus}
              activeSeries={activeSeries}
              setSerieHistoryFocus={setSerieHistoryFocus}
              singleMatches={singleMatches}
              plannedMatches={plannedMatches}
              plannedMatchBookings={bookings}
              completePlannedMatch={(id: any, data: any) =>
                completePlannedMatch(id, data, addSingleMatch)
              }
              cancelPlannedMatch={deletePlannedMatch}
              updatePlannedMatchPlayers={updatePlannedMatchPlayers}
              liveAllPlayers={liveAllPlayers}
              exitSetupToActivities={exitSetupToActivities}
              setView={setView}
              singleMatchesReturnView={singleMatchesReturnView}
              addSingleMatch={addSingleMatch}
              updateSingleMatch={updateSingleMatch}
              deleteSingleMatch={deleteSingleMatch}
              persistNewPlayerImmediate={persistNewPlayerImmediate}
              Padel24Engine={Padel24Engine}
              archiveWithLive={archiveWithLive}
              eloAdjustments={eloAdjustments}
              addPlayer={addPlayer}
              adoptPlayer={adoptPlayer}
              updatePlayer={updatePlayer}
              deletePlayer={deletePlayer}
              getPlayerTournamentHistory={getPlayerTournamentHistory}
              calcPlayerPositionInTournament={calcPlayerPositionInTournament}
            />
            {/* createTournament / miniTournaments / otherFormats -> src/app/views/HomeHubViews (PR-AG) */}
            <KMViews
              onRecordKMResult={recordKMResult}
              adoptServerActivity={adoptServerActivity}
              view={view}
              Padel24Engine={Padel24Engine}
              swapPairsInTournament={swapPairsInTournament}
              movePairInTournament={movePairInTournament}
              addGroupInTournament={addGroupInTournament}
              deleteGroupInTournament={deleteGroupInTournament}
              findDuplicatePlayerInDivision={findDuplicatePlayerInDivision}
              nextWeekendDates={nextWeekendDates}
              KM_DEFAULT_SHOWCASE={KM_DEFAULT_SHOWCASE}
              DEFAULT_RANKING_KM={DEFAULT_RANKING_KM}
              RANKING_OPTIONS_KM={RANKING_OPTIONS_KM}
              KM_CLASS_LETTERS={KM_CLASS_LETTERS}
              KM_AVAILABLE_CATEGORIES={KM_AVAILABLE_CATEGORIES}
              KM_COURT_PRIORITY_OPTIONS={KM_COURT_PRIORITY_OPTIONS}
              DEFAULT_KM_COURT_PRIORITY={DEFAULT_KM_COURT_PRIORITY}
              KM_PACING_OPTIONS={KM_PACING_OPTIONS}
              effectivePlayerDB={effectivePlayerDB}
              setupFromActivities={setupFromActivities}
              clubInfo={clubInfo}
              editingKMSetup={editingKMSetup}
              persistNewPlayerImmediate={persistNewPlayerImmediate}
              bookings={bookings}
              showToast={showToast}
              bookingsRef={bookingsRef}
              addManyBookings={addManyBookings}
              deleteManyBookings={deleteManyBookings}
              persistActivities={persistActivities}
              activities={activities}
              setEditingKMSetup={setEditingKMSetup}
              setActivitiesOpenId={setActivitiesOpenId}
              setView={setView}
              exitSetupToActivities={exitSetupToActivities}
              tournament={tournament}
              parkCurrentTournament={parkCurrentTournament}
              nextDisplayTournamentId={nextDisplayTournamentId}
              pausedTournaments={pausedTournaments}
              archive={archive}
              setTournament={setTournament}
              applyKMResult={applyKMResult}
              applyKMRandomResults={applyKMRandomResults}
              computeKMClassStandings={computeKMClassStandings}
              rebuildKnockoutForDivision={rebuildKnockoutForDivision}
              assignPairToGroup={assignPairToGroup}
              unassignPairInTournament={unassignPairInTournament}
              ALL_CLASSES={ALL_CLASSES}
              goHome={goHome}
              archiveTournament={archiveTournament}
              setProfilePlayerId={setProfilePlayerId}
              resumeArchivedTournament={resumeArchivedTournament}
              handlePauseTournament={handlePauseTournament}
            />
            <SeriespillViews
              adoptServerSeries={adoptServerSeries}
              onRecordSerieResult={recordSerieResult}
              adoptServerActivity={adoptServerActivity}
              view={view}
              effectivePlayerDB={effectivePlayerDB}
              clubInfo={clubInfo}
              pausedSeries={pausedSeries}
              handleResumeSeries={handleResumeSeries}
              handleDeletePausedSeries={handleDeletePausedSeries}
              persistNewPlayerImmediate={persistNewPlayerImmediate}
              setView={setView}
              showToast={showToast}
              updateActiveSeries={updateActiveSeries}
              Padel24Engine={Padel24Engine}
              activeSeries={activeSeries}
              applySerieResult={applySerieResult}
              recalcKMElo={recalcKMElo}
              goHome={goHome}
              activities={activities}
              setActivitiesOpenId={setActivitiesOpenId}
              serieFocusMatchId={serieFocusMatchId}
              setSerieFocusMatchId={setSerieFocusMatchId}
              setSerieBooking={setSerieBooking}
              persistActivities={persistActivities}
              archiveTournament={archiveTournament}
              setProfilePlayerId={setProfilePlayerId}
              allPlayers={allPlayers}
              setAllPlayers={setAllPlayers}
              handlePauseSeries={handlePauseSeries}
            />
            {/* singleMatches -> src/app/views/LibraryViews (PR-AG) */}
            <BookingActivityBanepadViews
              view={view}
              staffRefund={staffRefund}
              bookings={bookings}
              bookingInitialDate={bookingInitialDate}
              clubInfo={clubInfo}
              bookingZones={bookingZones}
              bookingConfig={bookingConfig}
              bookingHoursSaved={bookingHoursSaved}
              serieBooking={serieBooking}
              activeSeries={activeSeries}
              updateActiveSeries={updateActiveSeries}
              showToast={showToast}
              setSerieBooking={setSerieBooking}
              setView={setView}
              updateBookingZones={updateBookingZones}
              updateBookingConfig={updateBookingConfig}
              // #271: the court-state command has ALREADY written the config on
              // the server. This only refreshes the copy on screen — routing it
              // through updateBookingConfig would write the document straight
              // back, which is what the server owning the subtree prevents.
              applyCourtStateConfig={setBookingConfig}
              assets={assets}
              assetBookings={assetBookings}
              addAssetBooking={(intent) =>
                addAssetBooking(withOverrideRight(intent, mayOverrideConstraints), bookingConfig)
              }
              updateAssetBooking={(id, patch) =>
                updateAssetBooking(
                  id,
                  withOverrideRight(patch, mayOverrideConstraints),
                  bookingConfig,
                )
              }
              deleteAssetBooking={removeAssetBooking}
              saveAsset={saveAsset}
              deleteAsset={removeAsset}
              addBooking={addBooking}
              addManyBookings={addManyBookings}
              deleteManyBookings={async (ids) => {
                // Equipment attached to a court booking is deleted WITH it on the
                // server (docs/domains/ASSET_BOOKING.md); the screen's copy follows.
                const r = await deleteManyBookingsAndPlans(ids);
                if (r?.success) await dropAssetBookingsForBookings(ids);
                return r;
              }}
              replaceSeries={replaceSeries}
              updateBooking={updateBooking}
              deleteBooking={async (id) => {
                const r = await deleteBookingAndPlans(id);
                if (r?.success) await dropAssetBookingsForBookings([id]);
                return r;
              }}
              registerPlannedMatch={addPlannedMatch}
              createLinkedActivity={createLinkedActivity}
              plannedMatches={plannedMatches}
              completeSingleMatchActivity={completeSingleMatchActivity}
              recordSingleMatchLiveScore={recordSingleMatchLiveScore}
              activities={activities}
              setActivitiesOpenId={setActivitiesOpenId}
              setSerieFocusMatchId={setSerieFocusMatchId}
              effectivePlayerDB={effectivePlayerDB}
              reopenActivityPicker={reopenActivityPicker}
              setReopenActivityPicker={setReopenActivityPicker}
              persistNewPlayerImmediate={persistNewPlayerImmediate}
              persistActivities={persistActivities}
              activitiesOpenId={activitiesOpenId}
              openActivityReservation={openActivityReservation}
              openActivitySetup={openActivitySetup}
              Storage={Storage}
              bookingsRef={bookingsRef}
              Padel24Engine={Padel24Engine}
              validateBooking={validateBooking}
              setPendingSetupLoad={setPendingSetupLoad}
              setSetupFromActivities={setSetupFromActivities}
              setCustomActivityType={setCustomActivityType}
              setEditingKMSetup={setEditingKMSetup}
              estimateSchedule={estimateSchedule}
              makeGroups={makeGroups}
              buildFullSchedule={buildFullSchedule}
              drawPhase1Matches={drawPhase1Matches}
              fixPlayerBackToBackConflicts={fixPlayerBackToBackConflicts}
              ROUNDS_PER_PHASE={ROUNDS_PER_PHASE}
              initStandings={initStandings}
              parkCurrentTournament={parkCurrentTournament}
              nextDisplayTournamentId={nextDisplayTournamentId}
              pausedTournaments={pausedTournaments}
              archive={archive}
              tournament={tournament}
              setTournament={setTournament}
              recordResultForTournament={recordResultForTournament}
              recordKMResultForTournament={recordKMResultForTournament}
              recordKMLiveScore={recordKMLiveScore}
              recordPadLiveScore={recordPadLiveScore}
              recordSerieResultForTournament={recordSerieResultForTournament}
              recordSerieLiveScore={recordSerieLiveScore}
              banepadTournamentView={banepadTournamentView}
              americanoMatchToSlots={americanoMatchToSlots}
              playEndSound={playEndSound}
            />
            <AmericanoSetupViews
              adoptServerActivity={adoptServerActivity}
              adoptServerActivities={adoptServerActivities}
              view={view}
              PLAYER_DB={seedFallbackPlayers(PLAYER_DB)}
              playEndSound={playEndSound}
              speakWarning={speakWarning}
              DEFAULT_RANKING_AMERICANO={DEFAULT_RANKING_AMERICANO}
              RANKING_OPTIONS_AMERICANO={RANKING_OPTIONS_AMERICANO}
              effectivePlayerDB={effectivePlayerDB}
              customActivityType={customActivityType}
              persistNewPlayerImmediate={persistNewPlayerImmediate}
              archive={archive}
              clubInfo={clubInfo}
              exitSetupToActivities={exitSetupToActivities}
              setView={setView}
              openHelp={openHelp}
              pendingSetupLoad={pendingSetupLoad}
              setPendingSetupLoad={setPendingSetupLoad}
              syncBookingsFromPanel={syncBookingsFromPanel}
              activities={activities}
              persistActivities={persistActivities}
              showToast={showToast}
              Padel24Engine={Padel24Engine}
              parkCurrentTournament={parkCurrentTournament}
              nextDisplayTournamentId={nextDisplayTournamentId}
              pausedTournaments={pausedTournaments}
              tournament={tournament}
              setTournament={setTournament}
              setActivitiesOpenId={setActivitiesOpenId}
              bookings={bookings}
              setSeriesEditPending={setSeriesEditPending}
              addManyBookings={addManyBookings}
              handleReserveTournament={handleReserveTournament}
              buildActivityOnCreate={buildActivityOnCreate}
            />
            <AmericanoLiveViews
              view={view}
              tournament={tournament}
              Padel24Engine={Padel24Engine}
              applyAmericanoResult={applyAmericanoResult}
              onRecordResult={recordAmericanoResult}
              americanoMatchToSlots={americanoMatchToSlots}
              playEndSound={playEndSound}
              speakWarning={speakWarning}
              DEFAULT_RANKING_AMERICANO={DEFAULT_RANKING_AMERICANO}
              RANKING_OPTIONS_AMERICANO={RANKING_OPTIONS_AMERICANO}
              resumeArchivedTournament={resumeArchivedTournament}
              openHelp={openHelp}
              setTournament={setTournament}
              goHome={goHome}
              parkCurrentTournament={parkCurrentTournament}
              archiveTournament={archiveTournament}
              showToast={showToast}
              handlePauseTournament={handlePauseTournament}
              liveAllPlayers={liveAllPlayers}
              setProfilePlayerId={setProfilePlayerId}
            />
            {/* help / changelog / engine -> src/app/views/InfoViews (PR-AG). bugs stays
            (renders the BugArchiveView from src/components/bugs). */}
            <InfoViews
              view={view}
              setView={setView}
              helpReturnView={helpReturnView}
              helpInitialFormat={helpInitialFormat}
              goHome={goHome}
              Padel24Engine={Padel24Engine}
            />
            {view === 'bugs' && <BugArchiveView onBack={goHome} />}
            {/* MIN KONTO (#383). The account comes from the club session the app
              already holds — signing in returned it, so asking the server
              again would be a request for data in hand. Sign-out goes through
              the SAME `onLogout` the header uses, which ends every customer
              session upstream (#752): one path, so there is only one thing to
              get right. */}
            {view === 'account' && (
              <AccountView
                account={clubSession.state().account}
                scopes={clubSession.state().scopes}
                onSignOut={onLogout}
              />
            )}
            {/* TILGANGER (slice 3, 2026-10-18). The organization's own access
              page, opened from its settings by an access manager. The name
              comes from the scope the portal is acting in — `tenants.name`,
              the canonical one (#766) — with the club-info blob as the
              fallback outside API mode. The server guards the route; a refusal
              renders as a state, not as a crash. */}
            {view === 'access' && (
              <OrganizationAccessView
                organizationName={clubSession.state().currentScope?.name ?? clubInfo?.name ?? null}
                currentUserId={sessionAccountId}
                onBack={() => setView('clubSettings')}
              />
            )}
            {/* SAMARBEID (slice 4a, 2026-10-20). The organization's own
              co-operation page, opened from its settings by whoever manages
              co-operation — Eier by default. Same naming and the same
              server-side guard as «Tilganger» above. */}
            {/* BOOK BANER HOS PARTNERSENTERET (slice 4c, 2026-10-20). A club books
              a centre's courts through a co-operation. The club it is for is
              always named, so an account in two clubs never meets the server's
              «which club?» refusal; the server decides the rest. */}
            {view === 'linkBooking' && linkHost && (
              <LinkBookingView
                hostId={linkHost.hostTenantId}
                hostName={linkHost.hostName}
                homeId={linkHost.homeTenantId}
                onBack={() => setView('home')}
              />
            )}
            {/* AKTIVITETER HOS KLUBBEN (slice 4d, 2026-10-21). A centre arranges
              activities in a club's name through a co-operation; the club is
              named the same way, and the server decides the rest. */}
            {view === 'linkActivities' && activityHost && (
              <LinkActivitiesView
                hostId={activityHost.hostTenantId}
                hostName={activityHost.hostName}
                homeId={activityHost.homeTenantId}
                onBack={() => setView('home')}
              />
            )}
            {/* LOGG (#935, 2026-11-01). The organization's own audit trail, opened
              from its settings by whoever reads it — Eier and Administrator by
              default. The server guards the route and decides what is shown. */}
            {view === 'auditLog' && (
              <OrganizationAuditView
                organizationName={clubSession.state().currentScope?.name ?? clubInfo?.name ?? null}
                onBack={() => setView('clubSettings')}
              />
            )}
            {view === 'links' && (
              <OrganizationLinksView
                organizationName={clubSession.state().currentScope?.name ?? clubInfo?.name ?? null}
                onBack={() => setView('clubSettings')}
              />
            )}
            <MobileAppViews
              view={view}
              messagingEnabled={canUseMessaging}
              // The player app's club context, threaded rather than reached for
              // — the same path `onLogout` takes. `onSelectClub` is null on
              // every surface without a context to choose, and the app then
              // behaves as it always has.
              activeClub={activeClub}
              onSelectClub={onSelectClub}
              organizations={organizations}
              onEnterOrganization={onEnterOrganization}
              // THE APP'S OWN «LOGG UT». The native shell has no header, and on
              // the player surface neither does the browser — the app shows it
              // only to a person the server named, never on the portal preview.
              onSignOut={onLogout}
              // ── THE CLUB LOAD'S OWN STATE (#801 review) ─────────────
              //
              // `useMemberBootstrap` has reported `{status, error}` since #185
              // and NOTHING READ IT — so the workspace rendered before
              // `/member/bootstrap` answered and after it failed, in both cases
              // as an empty club. Worse, the identity states added in #800 then
              // MISDIAGNOSED it: `member` resolves against the club roster, so
              // an empty roster made a person whose profile exists read as
              // «you have no player profile yet».
              //
              // Passed down rather than branched here, so all four states —
              // loading, club-load failed, identity failed, no profile — live
              // in one component with one visual language and one ordering.
              memberBootstrap={memberBootstrap}
              standalone={standaloneMobile}
              effectivePlayerDB={effectivePlayerDB}
              bookings={bookings}
              plannedMatches={plannedMatches}
              singleMatches={singleMatches}
              activities={activities}
              archiveWithLive={archiveWithLive}
              memberHistory={memberHistory}
              memberMe={memberMe}
              bookingConfig={bookingConfig}
              bookingZones={bookingZones}
              clubInfo={clubInfo}
              addBooking={addBooking}
              // The venue's equipment, offered beside a court (#911 slice 2) — and
              // the member's own command for a machine booked on its own (§ 6b),
              // priced by the server from the stored configuration.
              assets={assets}
              assetBookings={assetBookings}
              addAssetBooking={(intent: any) => addMemberAssetBooking(intent, bookingConfig)}
              persistActivities={persistActivities}
              applyServerActivity={applyServerActivity}
              recordResultForTournament={recordResultForTournament}
              recordKMResultForTournament={recordKMResultForTournament}
              addPlannedMatch={addPlannedMatch}
              updatePlannedMatchPlayers={updatePlannedMatchPlayers}
              deletePlannedMatch={deletePlannedMatch}
              completePlannedMatch={(id: any, data: any) =>
                completePlannedMatch(id, data, addSingleMatch)
              }
              goHome={goHome}
            />
            <ReportingViews
              view={view}
              bookings={bookings}
              activities={activities}
              archiveWithLive={archiveWithLive}
              effectivePlayerDB={effectivePlayerDB}
              clubInfo={clubInfo}
              bookingConfig={bookingConfig}
              goHome={goHome}
            />
            <MessagesViews
              view={canUseMessaging ? view : ''}
              messaging={messaging}
              effectivePlayerDB={effectivePlayerDB}
              clubInfo={clubInfo}
              goHome={goHome}
            />
            <TournamentWizardViews
              view={view}
              Padel24Engine={Padel24Engine}
              PLAYER_DB={seedFallbackPlayers(PLAYER_DB)}
              RANKING_OPTIONS_PADEL24={RANKING_OPTIONS_PADEL24}
              defaultPadel24Dates={defaultPadel24Dates}
              exitSetupToActivities={exitSetupToActivities}
              goHome={goHome}
              handleWizardComplete={handleWizardComplete}
              effectivePlayerDB={effectivePlayerDB}
              archive={archive}
              clubInfo={clubInfo}
              openHelp={openHelp}
              persistNewPlayerImmediate={persistNewPlayerImmediate}
              pendingSetupLoad={pendingSetupLoad}
              setPendingSetupLoad={setPendingSetupLoad}
              activities={activities}
              bookings={bookings}
              showToast={showToast}
              bookingsRef={bookingsRef}
              addManyBookings={addManyBookings}
              deleteManyBookings={deleteManyBookings}
              persistActivities={persistActivities}
              setActivitiesOpenId={setActivitiesOpenId}
              setView={setView}
              tournament={tournament}
              setTournament={setTournament}
              resumeArchivedTournament={resumeArchivedTournament}
              savePreviewGroupsToActivity={savePreviewGroupsToActivity}
              handlePauseTournament={handlePauseTournament}
              setProfilePlayerId={setProfilePlayerId}
              parkCurrentTournament={parkCurrentTournament}
              resetTournament={resetTournament}
              archiveTournament={archiveTournament}
              playEndSound={playEndSound}
              applyPadel24Result={applyPadel24Result}
              onRecordResult={recordPadel24Result}
            />
            {/* tournament view -> TournamentWizardViews (PR-AJ) */}
          </main>
        </div>
        {/* end flex wrapper (max-w-6xl + sidebar + main) */}

        {/* Global overlays (profile modal, court-reservation modal, bug button,
          toast, serie-edit modals) -> src/app/GlobalOverlays (PR-BH) */}
        <GlobalOverlays
          profilePlayerId={profilePlayerId}
          setProfilePlayerId={setProfilePlayerId}
          effectivePlayerDB={effectivePlayerDB}
          archiveWithLive={archiveWithLive}
          singleMatches={singleMatches}
          eloAdjustments={eloAdjustments}
          view={view}
          openArchived={openArchived}
          setProfileReturn={setProfileReturn}
          Padel24Engine={Padel24Engine}
          courtReservation={courtReservation}
          reservationDraft={reservationDraft}
          setReservationDraft={setReservationDraft}
          activities={activities}
          closeActivityReservation={closeActivityReservation}
          syncBookingsFromPanel={syncBookingsFromPanel}
          tournament={tournament}
          archive={archive}
          allPlayers={allPlayers}
          setView={setView}
          toast={toast}
          setToast={setToast}
          seriesEditPending={seriesEditPending}
          setSeriesEditPending={setSeriesEditPending}
          seriesEditConfirm={seriesEditConfirm}
          setSeriesEditConfirm={setSeriesEditConfirm}
          applySeriesEdit={applySeriesEdit}
          hideBugButton={standaloneMobile}
          // Straight through from the auth gate: it owns the session the persona
          // switch adopts, and GlobalOverlays owns the button it lives on.
          devPersona={devPersona}
          messaging={canUseMessaging ? messaging : undefined}
          clubInfo={clubInfo}
          onOpenMessagesView={() => navigateWithAutoPause('messages')}
          dockOpen={messagesDockOpen}
          onToggleDock={() => setMessagesDockOpen((v) => !v)}
        />
      </div>
    </OverrideRightProvider>
  );
}
